import {
  useState, useEffect, useCallback,
} from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ArticleIcon from '@mui/icons-material/Article';
import {
  Edit,
  Warning,
  RemoveModerator,
} from '@mui/icons-material';
import { User } from 'lib/types/users';
import { NETWORKS, NETWORK_IDS, POSTING_ACCOUNT_TYPE } from 'lib/constants';
import { postingAccountToUrl, userIsAdmin, formatAccountName } from 'lib/helpers';
import { NetworkIcon } from 'elements/NetworkIcon';
import { omit } from 'lodash';
import { useSnackbar } from 'notistack';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { ResumeUpload } from 'features/JobDetail/ResumeUpload';
import { MobileTooltip } from 'components/MobileTooltip';
import {
  AutoCredit, PostingAccount, PostingAccountType,
} from 'lib/types/postingAccounts';
import { OwnerManagerSelect } from 'components/OwnerManagerSelect';
import { Switch } from '@mui/material';
import { useRouter } from 'next/router';
import { Banner } from 'components/Banner';
import { SettingsTabs } from '../SettingsTabs';
import { AutoCreditModal } from './AutoCreditModal';
import { AddSocialNetworkModal } from './AddSocialNetworkModal';
import { ConnectButton } from './ConnectButton';

interface Props {
  /**
   * The user who is having their connections updated.
   * Usually this is the current user, but in the case of teams it could be an account managed
   * by a team manager.
   */
  user: User;
}

/** We exclude the personal URL and snapchat from the network IDs array */
export const ALLOWED_NETWORK_IDS = omit(NETWORK_IDS, ['personal', 'snapchat']);

const resetConfirmationModal = {
  isOpen: false,
  onYes: () => {},
  onNo: () => {},
};

export const Accounts = ({ user }: Props) => {
  const API = useApi();
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  const [postingAccounts, setPostingAccounts] = useState<PostingAccount[]>([]);
  const [resumeUrl, setResumeUrl] = useState<string | undefined>(() => user?.resumeUrl);
  const [hidePersonalPosts, setHidePersonalPosts] = useState(
    () => currentUser?.settings?.hidePersonalPosts,
  );

  /** Keep a local user that gets updated when social links are updated */
  const [_user, setUser] = useState<User>(user);

  // Modal state management
  const [autoCreditModal, setAutoCreditModal] = useState<{
    isOpen: boolean;
    postingAccountId?: number;
    network?: number;
    username?: string;
    autoCredit?: AutoCredit;
  }>({ isOpen: false });

  const [addSocialNetworkModal, setAddSocialNetworkModal] = useState<{
    isOpen: boolean;
    network?: number;
  }>({ isOpen: false });

  const [confirmationModal, setConfirmationModal] = useState<any>({
    isOpen: false,
    title: '',
    text: '',
    onYes: () => {},
    onNo: () => {},
  });

  const getPostingAccounts = useCallback(async () => {
    const pa = await API.getPostingAccounts();
    setPostingAccounts(pa);
  }, [API]);

  useEffect(() => {
    if (user?.id) {
      getPostingAccounts();
    }
  }, [user?.id, getPostingAccounts]);

  const syncSocialLinkForPostingAccount = async (postingAccountId: number) => {
    const updatedUser = await API.syncSocialLinkForPostingAccount(postingAccountId);
    setUser(updatedUser);

    await getPostingAccounts();
  };

  const deleteSocialLinkViaPostingAccount = async (postingAccountId: number) => {
    const updatedUser = await API.deleteSocialLinkViaPostingAccount(postingAccountId);
    setUser(updatedUser);

    await getPostingAccounts();
  };

  const clearResume = async () => {
    const updatedUser = await API.clearResume();
    setResumeUrl(undefined);
    setUser(updatedUser);
  };

  const addResume = async (url: string) => {
    const updatedUser = await API.updateDefaultResume(url);
    setResumeUrl(url);
    setUser(updatedUser);
  };

  const addAutoCredit = async (
    postingAccountId: number, network: number, username: string, autoCredit?: AutoCredit,
  ) => {
    setAutoCreditModal({
      isOpen: true,
      postingAccountId,
      autoCredit,
      network,
      username,
    });
  };

  const NewAccountSection = () => (
    <div className="bg-white rounded-lg relative border-t">
      <div className="flex items-center justify-center m-4 font-semibold">
        Click a social network icon below to add an account
      </div>
      <div className="flex items-center justify-center gap-6 m-4 flex-wrap">
        {Object.values(ALLOWED_NETWORK_IDS).map((network) => (
          <MobileTooltip key={`add-${network}`} title={`Add ${NETWORKS[network].title} account`}>
            <button
              className="p-2 rounded-lg border shadow-sm"
              type="button"
              key={`btn-add-${network}`}
              onClick={() => {
                setAddSocialNetworkModal({
                  isOpen: true,
                  network,
                });
              }}
            >
              <NetworkIcon network={network} iconColor="color" />
            </button>
          </MobileTooltip>
        ))}
      </div>
    </div>
  );

  const ResumeCard = () => {
    const currentResumeUrl = resumeUrl || _user?.resumeUrl;
    const [isLoading, setIsLoading] = useState(false);

    const handleViewResume = useCallback(async () => {
      if (!currentResumeUrl || !_user?.id) return;

      setIsLoading(true);
      try {
        const result = await API.getResumeSignedUrl(_user.id);
        if (result?.signedUrl) {
          window.open(result.signedUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        console.error('Failed to get signed URL:', error);
      } finally {
        setIsLoading(false);
      }
    }, [currentResumeUrl]);

    return (
      <div className="bg-white rounded-lg p-2 relative border shadow-md h-full">
        <div className="flex-1 flex flex-col h-full">

          <div className="flex items-center mb-4">
            <ArticleIcon />
            <span className="ml-2 font-semibold text-sm">
              My resume
            </span>
          </div>

          <div className="flex items-center align-middle justify-center gap-2 mb-2">
            {!!currentResumeUrl
              && (
                <div className="btn-primary-outlined-xs">
                  <button
                    type="button"
                    onClick={handleViewResume}
                    disabled={isLoading}
                    className="text-orange hover:text-darkorange"
                  >
                    {isLoading ? 'Loading...' : 'View resume'}
                  </button>
                </div>
              )}
            <div className="btn-primary-outlined-xs">
              <ResumeUpload onUpload={addResume} miniMode text={currentResumeUrl ? 'Update resume' : 'Add resume'} />
            </div>
          </div>

          {!!currentResumeUrl && (
            <div className="flex justify-end space-x-2 mt-auto pt-2 border-t">
              <div className="mr-4">
                <button
                  aria-label="Delete resume"
                  type="button"
                  onClick={() => clearResume()}
                >
                  <span className="text-mediumgray hover:text-orange">
                    <span className="text-xs mr-1 font-semibold">
                      Delete
                    </span>
                    <DeleteIcon color="inherit" />
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const AutoImportButton = ({ account }: { account: PostingAccount }) => {
    const isDisabled = !account.networkAccountId
    || account.type === POSTING_ACCOUNT_TYPE.manager
    || !account.networkAccount?.id
    || account.network === NETWORK_IDS.linkedin;
    const tooltip = isDisabled
      ? 'You may only change auto import settings for your personal accounts or for accounts your team manages.'
      : 'Should Gondola attempt to import all new posts from this account?';
    return (

      <div className="flex items-center justify-between text-sm">
        <span>Auto import</span>
        <MobileTooltip title={tooltip} arrow placement="bottom-end">

          <div>
            <Switch
              aria-label="update auto import"
              checked={!!account.networkAccount?.autoImport}
              onClick={() => toggleAutoImport(
                !!account.networkAccount?.autoImport, account.network, account.account,
              )}
              name={`autoImport${account.id}`}
              disabled={isDisabled}
              color="secondary"
              className="text-sm"
            />
          </div>
        </MobileTooltip>

      </div>
    );
  };

  const AutoCreditButton = ({ account }: { account: PostingAccount }) => {
    if (!account.canAutoCredit) {
      return null;
    }
    return (
      <div className="flex items-center justify-between text-sm">
        <MobileTooltip arrow title="Should Gondola automatically give you a credit on all posts from this account as soon as they are imported? This option is only available for accounts that match your username. Please contact support@gondola.cc if you would like us to enable auto crediting for other accounts.">
          <span>Auto credit</span>
        </MobileTooltip>
        <div>
          {account.autoCredit && (
            <button
              type="button"
              title="Edit auto credit role"
              onClick={() => addAutoCredit(
                account.id as number, account.network, account.account, account.autoCredit,
              )}
            >
              <span className="mr-2">{account.autoCredit?.role?.name}</span>
              <Edit fontSize="small" />
            </button>
          )}

          <Switch
            aria-label="Toggle auto credit"
            checked={!!account.autoCredit}
            onClick={() => toggleAutoCredit(
              account.id as number, account.network, account.account, account.autoCredit,
            )}
            name={`autoCredit${account.id}`}
            color="secondary"
          />
        </div>
      </div>
    );
  };

  const DeleteButton = ({ account }: { account: PostingAccount }) => (
    <button
      aria-label="delete account"
      type="button"
      onClick={() => deletePostingAccount(account)}
      className="flex items-center text-mediumgray hover:text-orange"
    >
      <span className="text-xs mr-1 font-semibold">
        Delete
      </span>
      <DeleteIcon fontSize="small" color="inherit" />
    </button>
  );

  const DisconnectButton = ({ account }: { account: PostingAccount }) => (
    <button
      aria-label="unauth-account"
      type="button"
      onClick={() => unAuthConnectedAccount(account)}
      disabled={account.network === NETWORK_IDS.twitter}
      className={`flex items-center text-mediumgray ${account.network !== NETWORK_IDS.twitter && 'hover:text-orange'}`}
      title="You have authorized Gondola to access this account. This will disallow Gondola from accessing this account on your behalf."
    >
      <span className="text-xs mr-1 font-semibold">
        Disconnect
      </span>
      <RemoveModerator fontSize="small" color="inherit" />
    </button>
  );

  const ReAuthButton = ({ account }: { account: PostingAccount }) => (
    <div
      className="flex items-center gap-2"
    >
      <ConnectButton defaultLabel="Reconnect" network={account.network} />
      <MobileTooltip title={`We are unable to pull new content from the ${account?.account} page. Please update the connection's settings to resume importing this content.`}>
        <Warning fontSize="small" className="text-red" />
      </MobileTooltip>
    </div>
  );

  const CardFooter = ({ account }: { account: PostingAccount }) => {
    const shouldRenderDisconnectButton = !!hasConnections(account);
    const shouldRenderDeleteButton = !hasConnections(account);
    const shouldRenderReAuthButton = !!hasConnections(account)
      && !account?.connections?.some((c) => c.isTokenValid)
      && userIsAdmin(currentUser);

    if (!shouldRenderDisconnectButton && !shouldRenderDeleteButton
      && !shouldRenderReAuthButton) {
      return null;
    }
    return (
      <div className="flex items-center justify-end gap-4 mt-auto pt-2 border-t">
        {shouldRenderDeleteButton && <DeleteButton account={account} />}
        {shouldRenderDisconnectButton && <DisconnectButton account={account} />}
        {shouldRenderReAuthButton && <ReAuthButton account={account} />}
      </div>
    );
  };

  // TODO: figure out how to let the user know that they can't set an account to owner.
  // Right now it just gets set to manager.
  const AccountCard = ({ account }: { account: PostingAccount }) => (
    <div className="bg-white rounded-lg p-2 relative border shadow-md">
      <div className="flex-1 flex flex-col h-full">

        <div className="flex items-center mb-4 ">
          <NetworkIcon network={account.network} iconColor="color" />
          <span className={`ml-2 font-semibold ${account.account.length > 20 ? 'text-xs' : 'text-sm'}`}>
            <a href={postingAccountToUrl(account.network, account.account)} target="_blank" rel="noreferrer">
              {formatAccountName(account.account, account.network)}
            </a>
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <OwnerManagerSelect
            postingAccountId={account.id as number}
            type={account.type as PostingAccountType}
            updatePostingAccount={updatePostingAccount}
          />
        </div>
        <ShowOnProfileButton
          isVisible={!!account.showOnProfile}
          account={account}
        />
        {account.networkAccountId && <AutoImportButton account={account} />}
        <AutoCreditButton account={account} />
        <CardFooter account={account} />
      </div>

    </div>
  );

  const toggleAutoCredit = (
    postingAccountId: number, network: number, username: string, autoCredit?: AutoCredit,
  ) => {
    if (autoCredit) {
      // user clicks no
      setConfirmationModal({
        isOpen: true,
        title: 'Confirm',
        text: 'Are you sure you would like to turn off auto crediting for this account?',
        onNo: () => {
          setConfirmationModal(resetConfirmationModal);
        },
        onYes: async () => {
          if (!autoCredit.id) return;

          await API.deleteAutoCredit(autoCredit.id);
          getPostingAccounts();
          setConfirmationModal(resetConfirmationModal);
        },
      });
      return;
    }

    addAutoCredit(postingAccountId, network, username, autoCredit);
  };

  const toggleAutoImport = async (
    autoImport: boolean, network: number, account: string,
  ) => {
    if (!account) {
      return;
    }
    if (autoImport) {
      await API.autoImportOff(account, network);
    } else {
      await API.autoImportOn(account, network);
    }
    getPostingAccounts();
  };

  const unAuthConnectedAccount = (postingAccount: PostingAccount) => {
    const connectedAccount = postingAccount.connections && postingAccount?.connections.length > 0
      ? postingAccount.connections[0] : undefined;
    if (!connectedAccount) {
      return;
    }
    const { network } = postingAccount;
    const revokeByNetwork = async () => {
      if (network === NETWORK_IDS.tiktok && postingAccount.networkAccountId) {
        await API.revokeTikTokAuth(postingAccount.networkAccountId as number);
      } else if (network === NETWORK_IDS.twitch && postingAccount.networkAccountId) {
        await API.revokeTwitchAuth(postingAccount.networkAccountId as number);
      } else if (network === NETWORK_IDS.facebook) {
        await API.revokeFacebookAuth();
      } else {
        // a generic revoke that just removes the connection/tokens
        await API.deleteConnection(connectedAccount.id);
      }
      // if there is a disconnect query, clear it
      router.push({
        pathname: router.pathname,
        query: {},
      });
      getPostingAccounts();
    };

    setConfirmationModal({
      isOpen: true,
      title: 'Confirm',
      text: `Are you sure you would like revoke permissions for this ${NETWORKS[network].title} account?`,
      onNo: () => {
        setConfirmationModal(resetConfirmationModal);
      },
      onYes: async () => {
        revokeByNetwork();
        setConfirmationModal(resetConfirmationModal);
      },
    });
  };

  /** Show a confirmation modal and then delete the posting account */
  const deletePostingAccount = async (postingAccount: PostingAccount) => {
    const text = (
      <>
        <p>
          Are you sure you want to remove the
          {' '}
          <strong>{postingAccount.account}</strong>
          {' '}
          {NETWORKS[postingAccount.network].title}
          {' '}
          account?
        </p>
        {postingAccount.type === POSTING_ACCOUNT_TYPE.owner && (
          <p className="mt-2">
            {' '}
            If you simply want to hide the link
            from your profile page, toggle the
            {' '}
            <strong>Show on profile</strong>
            {' '}
            switch.
          </p>
        )}
      </>
    );
    setConfirmationModal({
      isOpen: true,
      title: 'Confirm deletion',
      text,
      onNo: () => {
        setConfirmationModal(resetConfirmationModal);
      },
      onYes: async () => {
        try {
          // First clear the social link from the user's account.
          await deleteSocialLinkViaPostingAccount(postingAccount.id as number);
          // Then delete the posting account record entirely.
          await API.deletePostingAccount(postingAccount.id as number);
          await getPostingAccounts();
          enqueueSnackbar('Your posting account has been removed from your account', {
            variant: 'success',
          });
        } catch (err) {
          enqueueSnackbar(`Error removing account: ${err}`, {
            variant: 'error',
          });
        }
        setConfirmationModal(resetConfirmationModal);
      },
    });
  };

  /** Update the posting account, setting type or showOnProfile */
  const updatePostingAccount = async (postingAccountId: number, type: PostingAccountType) => {
    try {
      if (!postingAccountId) {
        enqueueSnackbar('No account specified', { variant: 'error' });
        return;
      }
      await API.updatePostingAccount(postingAccountId, { type });
      getPostingAccounts();
      enqueueSnackbar('Account updated', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Error updating account: ${err}`, { variant: 'error' });
    }
  };

  /* allow delete when no connections */
  const hasConnections = (postingAccount: PostingAccount) => postingAccount?.connections?.length;

  const renderAccountsCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-6">
      {postingAccounts?.map((pa: PostingAccount) => (
        <AccountCard key={`${pa.network}- ${pa.account}`} account={pa} />
      ))}
      <ResumeCard />
    </div>
  );

  /** Render the button used top add/remove social links from the user's profile. */
  const ShowOnProfileButton = (
    { isVisible, account }: { isVisible: boolean; account: PostingAccount },
  ) => {
    const disabled = account.type === POSTING_ACCOUNT_TYPE.manager;
    const tooltip = disabled
      ? 'You may only show personal accounts on your profile.'
      : 'Should this account be visible on your profile? Only one account per network may be visible and it must be a personal account.';
    return (
      <div className="flex items-center justify-between text-sm">
        <span>Show on profile</span>
        <MobileTooltip title={tooltip} placement="top-end" arrow>
          <div>
            <Switch
              aria-label="update auto credit"
              checked={isVisible && !disabled}
              onClick={() => (isVisible
                ? deleteSocialLinkViaPostingAccount(account.id as number)
                : syncSocialLinkForPostingAccount(account.id as number))}
              name={`show-on-profile-${account.id}`}
              color="secondary"
              disabled={disabled}
            />
          </div>
        </MobileTooltip>

      </div>

    );
  };

  const toggleHidePersonalPosts = async () => {
    const newValue = !hidePersonalPosts;
    try {
      await API.updateUserSettings({ hidePersonalPosts: newValue });
      setHidePersonalPosts(newValue);
      enqueueSnackbar('Hide personal posts setting updated!', { variant: 'success' });
    } catch (err: any) {
      const msg = err?.message || 'Failed to update settings. Please try again later.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const renderDisconnectBanner = () => {
    if (router.query?.deactivation !== 'facebook') {
      return null;
    }

    const fbAccount = postingAccounts.find((a) => a.network === NETWORK_IDS.facebook);
    const callToAction = fbAccount ? (
      <button
        type="button"
        className="btn-primary"
        onClick={() => unAuthConnectedAccount(fbAccount)}
      >
        Disconnect
      </button>
    ) : null;

    return (
      <Banner
        message="To disconnect your Facebook account, click the disconnect button here or on the account card below."
        callToAction={callToAction}
      />
    );
  };

  const renderInvalidTokenBanner = () => {
    const postingAccountsWithInvalidTokens = postingAccounts
      ?.filter((pa) => pa.connections?.some((c) => !c.isTokenValid));

    if (!postingAccountsWithInvalidTokens?.length) {
      return null;
    }

    const buttons = postingAccountsWithInvalidTokens.map((pa) => (
      <ConnectButton defaultLabel="Reconnect" network={pa.network} />
    ));

    return (
      <Banner
        message="You have one or more expired connections to social media accounts. Please refresh your connection(s) so we can keep importing your posts and updating your posts' stats."
        callToAction={<div className="w-full flex items-center justify-center gap-2">{buttons}</div>}
      />
    );
  };

  return (
    <SettingsTabs selectedTab="accounts">
      {renderDisconnectBanner()}
      {renderInvalidTokenBanner()}
      <div className="max-w-screen-xl mx-auto">
        <div className="mt-6 mb-4">
          <h1 className="text-xl font-bold">My accounts</h1>
          <p className="my-4">
            These are the social accounts that are directly tied to your
            {' '}
            <strong>
              @
              {user.username}
            </strong>
            {' '}
            Gondola user.
            For each platform you may choose one social account
            to be the one that is displayed on your profile.
            By connecting your accounts to Gondola,
            we can automatically import posts, photos,
            and videos so you can get credits. You can add personal accounts to Gondola
            or you can add &quot;managed&quot; accounts. Managed accounts are the business/
            brand accounts you work on regularly.
          </p>
          <div>
            If you wish, we can hide all of your uncredited posts from your personal account.
            Toggling this means that nobody will see posts from your personal accounts as long
            as they remain uncredited.
            <br />
            Hide uncredited personal posts on your profile:
            <Switch
              checked={hidePersonalPosts}
              name="hidePersonalPosts"
              color="primary"
              onClick={toggleHidePersonalPosts}
            />
            {hidePersonalPosts ? 'Yes' : 'No'}
          </div>
          <NewAccountSection />
          {renderAccountsCards()}
        </div>
      </div>

      {/* Modals */}
      {autoCreditModal.isOpen && (
        <AutoCreditModal
          postingAccountId={autoCreditModal.postingAccountId!}
          autoCredit={autoCreditModal.autoCredit}
          network={autoCreditModal.network!}
          username={autoCreditModal.username!}
          user={currentUser}
          onAdd={async () => {
            getPostingAccounts();
            setAutoCreditModal({ isOpen: false });
          }}
          onClose={() => {
            setAutoCreditModal({ isOpen: false });
          }}
        />
      )}

      {addSocialNetworkModal.isOpen && (
        <AddSocialNetworkModal
          network={addSocialNetworkModal.network!}
          user={user}
          onClose={() => {
            setAddSocialNetworkModal({ isOpen: false });
            getPostingAccounts();
          }}
        />
      )}

      {confirmationModal.isOpen && (
        <ConfirmationModal
          text={confirmationModal.text}
          title={confirmationModal.title}
          onNo={confirmationModal.onNo}
          onYes={confirmationModal.onYes}
        />
      )}
    </SettingsTabs>
  );
};
